<!-- One line: name, plan chip and chevron. -->
<button
  #trigger
  mat-stroked-button
  class="console-field-button app-selector-trigger"
  cdkOverlayOrigin
  #origin="cdkOverlayOrigin"
  aria-haspopup="dialog"
  [attr.aria-expanded]="isOpen"
  (click)="toggle()"
>
  <span class="app-selector-name">{{ activeApp?.name || 'Select project' }}</span>

  @if (badgeFor(activeApp); as badge) {
    <fleet-badge [label]="badge.label" [tone]="badge.tone" [tooltip]="badge.tooltip ?? ''"></fleet-badge>
  }

  <mat-icon
    class="icon-size-5 app-selector-chevron"
    [svgIcon]="'heroicons_solid:chevron-down'"
  ></mat-icon>
</button>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="origin"
  [cdkConnectedOverlayOpen]="isOpen"
  [cdkConnectedOverlayPositions]="positions"
  [cdkConnectedOverlayPush]="true"
  [cdkConnectedOverlayViewportMargin]="16"
  cdkConnectedOverlayPanelClass="app-selector-overlay"
  (attach)="onAttached()"
  (detach)="close()"
  (overlayOutsideClick)="onOutsideClick($event)"
>
  <div class="app-selector-panel" role="dialog" aria-label="Switch project">
    <!-- Search. The ghost behind the input completes the first name that
         starts with what was typed; Tab or Right arrow accepts it. -->
    <div class="app-selector-search">
      <mat-icon
        class="icon-size-4 text-hint"
        [svgIcon]="'heroicons_outline:magnifying-glass'"
      ></mat-icon>
      <span class="app-selector-input-wrap">
        <span class="app-selector-ghost" aria-hidden="true">{{ suggestion }}</span>
        <input
          #searchInput
          [(ngModel)]="query"
          (ngModelChange)="onQueryChange()"
          (keydown)="onSearchKeydown($event)"
          placeholder="Search by name or ID"
          aria-label="Search projects"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded="true"
          aria-controls="app-selector-list"
          [attr.aria-activedescendant]="highlightedOption?.optionId"
          autocomplete="off"
          spellcheck="false"
        />
      </span>
      <kbd class="app-selector-kbd">Esc</kbd>
    </div>

    @if (productFilters.length) {
      <!--
        Icons only, so the row still fits once there are many products: the
        ODIN mark for All, each product's own mark otherwise. The active tab
        adds its name and count; the others say it in their tooltip.
      -->
      <div class="app-selector-filters" role="group" aria-label="Filter by product">
        @for (filter of productFilters; track filter.value) {
          @let active = productFilter === filter.value;
          <button
            type="button"
            class="app-selector-filter"
            [attr.aria-pressed]="active"
            [attr.aria-label]="filter.label + ', ' + filter.count + ' projects'"
            [matTooltip]="filter.label + ' (' + filter.count + ')'"
            [matTooltipDisabled]="active"
            (click)="setProductFilter(filter.value)"
          >
            @if (filter.value === 'all') {
              <svg class="app-selector-filter-icon" viewBox="0 0 793 867" aria-hidden="true">
                <path fill="currentColor" d="M771.57,587.53l-22.61-392.03L387.18.27l-252.79,136.68,253.38-92.18,319.25,179.6,6.89,363.55c-12.32,8.99-20.34,23.54-20.34,39.96,0,27.31,22.14,49.45,49.45,49.45s49.45-22.14,49.45-49.45c0-16.68-8.26-31.41-20.91-40.37Z" />
                <path fill="currentColor" d="M61,614.05l6.31-361.44c15.78-7.37,26.72-23.38,26.72-41.95,0-25.56-20.72-46.29-46.29-46.29S1.45,185.11,1.45,210.67c0,16.76,8.91,31.43,22.24,39.56L.08,643.07l387.43,223.38,250.29-144.79-250.81,64.88L61,614.05Z" />
              </svg>
            } @else {
              <mat-icon class="app-selector-filter-icon" [svgIcon]="'odin-' + filter.value"></mat-icon>
            }
            @if (active) {
              <span>{{ filter.label }}</span>
              <span class="app-selector-filter-count">{{ filter.count }}</span>
            }
          </button>
        }
      </div>
    }

    <div class="app-selector-list" id="app-selector-list" role="listbox" aria-label="Projects">
      @for (group of groups; track group.label) {
        @if (group.label) {
          <div class="app-selector-group-label" role="presentation">{{ group.label }}</div>
        }
        @for (option of group.options; track option.optionId) {
          <div
            class="app-selector-option"
            role="option"
            [id]="option.optionId"
            [attr.aria-selected]="option.app.id === activeApp?.id"
            [class.is-active]="option.app.id === activeApp?.id"
            [class.is-highlighted]="option === highlightedOption"
            [class.has-id-match]="option.idMatch"
            (mousemove)="option !== highlightedOption && highlight(options.indexOf(option))"
            (mousedown)="$event.preventDefault()"
            (click)="choose(option.app)"
          >
            @if (option.app.id === activeApp?.id) {
              <mat-icon class="icon-size-4 text-primary" [svgIcon]="'heroicons_solid:check'"></mat-icon>
            } @else {
              <mat-icon
                class="icon-size-4 text-hint"
                [svgIcon]="
                  option.app.type === 'sdk' || !option.app.type
                    ? 'heroicons_outline:cube'
                    : 'heroicons_outline:video-camera'
                "
              ></mat-icon>
            }

            <span class="app-selector-option-text">
              <span class="app-selector-option-name">
                @for (part of option.name; track $index) {
                  <span [class.app-selector-match]="part.match">{{ part.text }}</span>
                }
              </span>
              @if (option.idMatch) {
                <span class="app-selector-option-id">
                  <span class="app-selector-option-id-label">ID</span>
                  @for (part of option.idMatch; track $index) {
                    <span [class.app-selector-match]="part.match">{{ part.text }}</span>
                  }
                </span>
              }
            </span>

            <!-- Neutral states (External, Whitelisted) are quiet text; plan
                 states that need attention keep the coloured chip. -->
            @if (option.badge; as badge) {
              @if (badge.tone === 'neutral') {
                <span class="app-selector-state" [matTooltip]="badge.tooltip ?? ''">{{ badge.label }}</span>
              } @else {
                <fleet-badge [label]="badge.label" [tone]="badge.tone" [tooltip]="badge.tooltip ?? ''"></fleet-badge>
              }
            }
          </div>
        }
      }

      @if (!options.length) {
        <div class="app-selector-empty">
          @if (query.trim()) {
            No project matches <strong>“{{ query.trim() }}”</strong>.
            @if (canCreate) {
              <button type="button" class="app-selector-empty-action" (click)="createProject(query)">
                Create “{{ query.trim() }}” as a new project
              </button>
            }
          } @else {
            No projects yet.
          }
        </div>
      }
    </div>

    <div class="app-selector-actions">
      <a class="app-selector-action" [routerLink]="['/projects']" (click)="close()">
        <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:squares-2x2'"></mat-icon>
        All projects
      </a>
      @if (canCreate) {
        <button type="button" class="app-selector-action app-selector-action--primary" (click)="createProject()">
          <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:plus'"></mat-icon>
          New project
        </button>
      }
    </div>
  </div>
</ng-template>
